import { useDispatch, useSelector } from 'react-redux'
import {
    DashboardSyncEvents,
    DashboardSyncSettings,
    IWidget,
    WidgetProps,
    WidgetSettings
} from '../../widgetDashboard.interface'
import { forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { AppContext } from '../../../../App'
import { getIsWidgetSlotActiveSelector } from '../../../../selectors/widgetDashboard'
import { deferAfterPaneSelectionPaint } from '../../deferPaneSideEffects'
import store from '../../../../store'
import useSetPrimarySecurity from '../../../../hooks/useSetPrimarySecurity'
import { updateWidgetSettings, updateWidgetType } from '../../../../reducer/widgetDashboard.reducer'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { Box, Button, Stack, Typography } from '@mui/material'
import SettingsOutlinedIcon from '../../../../assets/icons/SettingOutlined'
import { NonInteractiveTooltip } from '../../../custom'
import { WidgetTypes } from '../../widgetDashboard.enums'
import { selectActiveLayoutId, selectExpandedRows } from '../../../../selectors/home'
import { Segment } from '../../../../proto/generated/security'
import { isExpressionMode } from '../../../../util/expression'
import { OptionChainViewKey } from '../../../../assets/constants'
import { selectUnderlyingOptionsTradeData } from '../../../../selectors/options'
import { setOptionChainExpandedRows } from '../../../../reducer/home.reducer'
import OptionChainTab from '../../../../pages/home/optionChainTab'
import OptionChainWidgetMetadata from './optionChainWidgetMetadata'
import ChartSettingsButtons from '../../../chartV2/chartContextMenuButton/ChartSettingsButtons'
import OptionChainWidgetSettingsDialog from './optionChainWidgetSettingsDialog'

export type OptionChainWidgetSettings = WidgetSettings<{
    chartId: string
}>

const OPTION_CHAIN_VIEW_KEY = OptionChainViewKey.WIDGET

const OptionChainWidget = forwardRef<any, WidgetProps<OptionChainWidgetSettings>>(({
    dashboardId,
    slotId,
    settings,
    allowSplit
}, ref) => {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const chartId = settings.chartId ?? `${dashboardId}-${slotId}`
    const setPrimarySecurity = useSetPrimarySecurity()
    const removed = useRef(false)
    const { key, expressionMode, securities } = settings
    const [loading, setLoading] = useState(true)
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const [openSettings, setOpenSettings] = useState(false)
    const isActiveSlot = useSelector(getIsWidgetSlotActiveSelector(dashboardId, slotId))
    const previousSecurityInfo = useRef({ key, expressionMode, securities })
    const mounted = useRef(false)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const expandedRows = useSelector(selectExpandedRows(OPTION_CHAIN_VIEW_KEY))

    useEffect(() => {
        if (!key) return
        let cancelled = false
        setLoading(true)
        if (isExpressionMode(key)) {
            setLoading(false)
            return
        }
        getSecurityAsync(key).then(async (newSecurity) => {
            if (cancelled) return
            if (!newSecurity) {
                setSecurity(undefined)
                setLoading(false)
                return
            }
            if ([Segment.FO, Segment.CD].includes(newSecurity.segment)) {
                const underlying = await getSecurityAsync(getSecurityKey(newSecurity.exchange, newSecurity.underlyingSecurityCode ?? ''))
                if (!cancelled) setSecurity(underlying ?? newSecurity)
            } else {
                if (!cancelled) setSecurity(newSecurity)
            }
            if (!cancelled) setLoading(false)
        })
        return () => { cancelled = true }
    }, [key])

    const underlyingToken = useMemo(() => {
        return getSecurityKey(security?.exchange ?? 0, security?.code ?? '')
    }, [security])

    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken || key))
    const initializedExpandedRowsFor = useRef('')

    useEffect(() => {
        if (!underlyingToken) return
        if (initializedExpandedRowsFor.current === underlyingToken) return
        initializedExpandedRowsFor.current = underlyingToken
        const firstNonDerivedIdx = optionExpiries?.findIndex((d) => !d.derived) ?? -1
        const rowIndex = firstNonDerivedIdx >= 0 ? firstNonDerivedIdx : 0
        dispatch(setOptionChainExpandedRows({
            expandedRows: [rowIndex.toString()],
            optionChainViewKey: OPTION_CHAIN_VIEW_KEY
        }))
    }, [underlyingToken, dispatch])

    const handleExpiryChange = useCallback((index: number) => {
        const rowIndex = index >= 0 ? index : 0
        dispatch(setOptionChainExpandedRows({
            expandedRows: [rowIndex.toString()],
            optionChainViewKey: OPTION_CHAIN_VIEW_KEY
        }))
    }, [dispatch])

    useEffect(() => {
        mounted.current = false
    }, [underlyingToken])

    useEffect(() => {
        if (!isActiveSlot || !security || !underlyingToken || mounted.current) return
        mounted.current = true
        deferAfterPaneSelectionPaint(() => {
            const { key: currentKey, expressionMode: currentExpressionMode } =
                store.getState().homeStore.primarySecurity
            if (currentKey !== underlyingToken || currentExpressionMode !== expressionMode) {
                setPrimarySecurity(
                    underlyingToken,
                    [security],
                    expressionMode,
                    security?.displayName ?? security?.symbol
                )
            }
        })
    }, [isActiveSlot, security, underlyingToken, expressionMode, setPrimarySecurity])

    useImperativeHandle<any, IWidget>(
        ref,
        () => ({
            onActive() {
                if (!security || !underlyingToken) return
                deferAfterPaneSelectionPaint(() => {
                    const { key: currentKey, expressionMode: currentExpressionMode } =
                        store.getState().homeStore.primarySecurity
                    if (currentKey !== underlyingToken || currentExpressionMode !== expressionMode) {
                        setPrimarySecurity(
                            underlyingToken,
                            [security],
                            expressionMode,
                            security?.displayName ?? security?.symbol
                        )
                    }
                })
            },
            updateSettings(settings: WidgetSettings<any>) {
                dispatch(updateWidgetSettings({
                    id: dashboardId,
                    slotId,
                    settings
                }))
            },
            onSyncSettingsChanged(settingKey: keyof DashboardSyncSettings, value: boolean) {
                if (settingKey === 'security' && value && underlyingToken) {
                    appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                        key: underlyingToken,
                        expressionMode,
                        securities: [underlyingToken]
                    })
                }
            },
            onRemove() {
                removed.current = true
            },
            openSettings() {
                setOpenSettings(true)
            },
            onSecurityUpdate: (data) => {
                const incomingKey = data.key
                if (!incomingKey || incomingKey === key || incomingKey === underlyingToken) return
                getSecurityAsync(incomingKey).then((incoming) => {
                    if (!incoming) return
                    let resolvedKey = getSecurityKey(incoming.exchange, incoming.code)
                    if ([Segment.FO, Segment.CD].includes(incoming.segment) && incoming.underlyingSecurityCode) {
                        resolvedKey = getSecurityKey(incoming.exchange, incoming.underlyingSecurityCode)
                    }
                    if (resolvedKey === underlyingToken) return
                    dispatch(updateWidgetSettings({
                        id: dashboardId,
                        slotId,
                        settings: {
                            ...data,
                            key: resolvedKey,
                            securities: [resolvedKey],
                        }
                    }))
                })
            },
            onTimeBucketUpdate: () => {},
            onCrosshairUpdate: () => {}
        }),
        [dashboardId, slotId, security, underlyingToken, expressionMode, isActiveSlot, appContext]
    )

    useEffect(() => {
        if (!isActiveSlot) return
        if (underlyingToken && underlyingToken !== previousSecurityInfo.current.key) {
            appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                key: underlyingToken,
                expressionMode,
                securities: [underlyingToken]
            })
            previousSecurityInfo.current = {
                key: underlyingToken,
                expressionMode,
                securities: [underlyingToken]
            }
        }
    }, [slotId, underlyingToken, expressionMode, isActiveSlot, appContext])

    const handleSecuritySelected = useCallback(
        (key: string, expressionMode: boolean, securities: string[]) => {
            dispatch(
                updateWidgetSettings({
                    id: dashboardId,
                    slotId,
                    settings: {
                        key,
                        expressionMode,
                        securities,
                    },
                })
            )
        },
        [dashboardId, dispatch, slotId]
    )

    if (!chartId) return null

    if (loading) {
        return (
            <Box
                sx={{
                    height: '100%',
                    width: '100%',
                    p: '2px',
                    boxSizing: 'border-box',
                    bgcolor: 'background.paper',
                }}
            />
        )
    }

    if (!security?.hasFo) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    p: '2px',
                    boxSizing: 'border-box',
                    background: (theme) => theme.palette.background.paper
                }}
            >
                <Stack gap={2}>
                    <Typography>F&O not present for this security</Typography>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            dispatch(updateWidgetType({
                                id: activeLayoutId,
                                slotId: Number(slotId),
                                type: WidgetTypes.CHART
                            }))
                        }}
                    >
                        Go to candle chart
                    </Button>
                </Stack>
            </Box>
        )
    }

    return (
        <Box
            id={`chart-${chartId}`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                minHeight: 0,
                position: 'relative',
                p: '2px',
                boxSizing: 'border-box',
                background: (theme) => theme.palette.background.paper
            }}
        >
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative', pt: '28px', boxSizing: 'border-box' }}>
                {security && (
                    <OptionChainWidgetMetadata
                        security={security}
                        expandedRows={expandedRows}
                        onExpiryChange={handleExpiryChange}
                        onSecuritySelected={handleSecuritySelected}
                    />
                )}
                <OptionChainTab
                    optionChainViewKey={OPTION_CHAIN_VIEW_KEY}
                    showSingleExpiry
                    disableRowCollapse
                    optionsTradeDataType="extended"
                    fetchEnabled={isActiveSlot}
                    widgetSecurityKey={underlyingToken}
                    widgetSecurity={security}
                />
                <NonInteractiveTooltip title="Settings" placement="left-start">
                    <Button
                        variant="text"
                        color="inherit"
                        disableRipple
                        onClick={() => setOpenSettings(true)}
                        sx={{
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            top: 0,
                            right: 0,
                            left: 'auto',
                            minWidth: 52,
                            width: 52,
                            height: 28,
                            minHeight: 28,
                            maxHeight: 28,
                            p: 0,
                            borderRadius: 0,
                            zIndex: 5
                        }}
                    >
                        <SettingsOutlinedIcon sx={{ width: 20, height: 20 }} />
                    </Button>
                </NonInteractiveTooltip>
            </Box>
            {security && (
                <ChartSettingsButtons allowSplit={allowSplit} viewKey={chartId} security={security} />
            )}
            <OptionChainWidgetSettingsDialog
                open={openSettings}
                onClose={() => setOpenSettings(false)}
                optionChainViewKey={OPTION_CHAIN_VIEW_KEY}
            />
        </Box>
    )
})

OptionChainWidget.displayName = 'OptionChainWidget'

export default memo(OptionChainWidget)
